import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { Plus } from 'lucide-react'
import { CreateAddonModal } from './CreateAddonModal'
import Loader from '../../../common/components/Loader'
import { Currency, Extras, Languages } from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { sanitizeDate } from '../../../core/constants/times'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  getAllExtras,
  publishExtras,
  unpublishExtras,
} from '../../../core/services/extras'
import {
  getCurrencies,
  getSettingsLanguages,
} from '../../../core/services/festival-settings'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { Icon } from '@/common/components/Icon'

export const AddonPage: React.FC = () => {
  const {
    size,
    setSize,
    page,
    setPage,
    setTotalElemenets,
    totalElements,
    sortBy,
    setSortBy,
  } = useTableParams({
    defaultSize: 10,
  })
  const { editionId, festivalName } = useParams()
  const [allLanguages, setAllLanguages] = useState<Languages[]>([])
  const { hasEditPermission } = useCheckPermission({ page: 'TICKETS_ADD_ONS' })

  const [editCreateModal, setEditCreateModal] = useState(false)
  const [currentAddonId, setCurrentAddonId] = useState<string>()
  const [editMode, setEditMode] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [addons, setAddons] = useState<Extras[]>([])
  const [currency, setCurrency] = useState<Currency | null>(null)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const [currencyResponse, allLanguagesResponse] = await Promise.all([
          getCurrencies(),
          getSettingsLanguages(),
        ])
        if (currencyResponse.status === 200 && allLanguagesResponse.status === 200) {
          setCurrency(currencyResponse.data.defaultCurrency)
          setAllLanguages(allLanguagesResponse.data)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const response = await getAllExtras({
        page,
        size,
        sortBy,
        sortDirection,
      })
      setTotalElemenets(response.data.totalElements)
      setAddons(response.data.content)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [size, page, editionId, festivalName, sortBy, sortDirection])

  useEffect(() => {
    getData()
  }, [getData, editionId, festivalName])

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'DESC') {
        setSortDirection('ASC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('DESC')
      setSortBy(sort)
    }
  }

  const handlePublish = (isCheck: string[]) => {
    // prettier-ignore
    (async () => {
      if (!hasEditPermission) {
        return
      }
      try {
        setLoading(true)
        const { status } = await publishExtras(isCheck)
        getData()
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Publish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const handleUnpublish = (isCheck: string[]) => {
    // prettier-ignore
    (async () => {
      if (!hasEditPermission) {
        return
      }
      try {
        setLoading(true)
        const { status } = await unpublishExtras(isCheck)
        getData()
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Unpublish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const columns: IColumn[] = [
    {
      title: 'Name',
      accessor: 'name',
    },
    {
      title: 'Description',
      accessor: 'description',
      sortable: true,
      render: (row) => {
        const plainText = row.description?.replace(/<[^>]*>/g, '') || ''
        const isExpanded = Boolean(expandedRows[row.id])
        const hasLongDescription = plainText.length > 220
        return (
          <div className="min-w-[260px]">
            <div className={isExpanded ? '' : 'line-clamp-3'}>{plainText}</div>
            {hasLongDescription && (
              <span
                onClick={() => {
                  setExpandedRows((prev) => ({
                    ...prev,
                    [row.id]: !prev[row.id],
                  }))
                }}
                className="text-accent-1 cursor-pointer inline-block mt-1"
              >
                {isExpanded ? '[show less]' : '[...]'}
              </span>
            )}
          </div>
        )
      },
    },
    {
      title: 'Created Date',
      accessor: 'createdDate',
      sortable: true,
      render: (row) => <>{sanitizeDate(row.createdDate)}</>,
    },
    {
      title: 'Status',
      accessor: 'status',
      sortable: true,
      style: (row) => ({
        color: `${row.status === 'ACTIVE' ? '#198754' : '#DC3545'}`,
      }),
    },
    {
      title: 'Published',
      accessor: '',
      render: (row) => {
        return (
          <Checkbox
            checked={row.published}
            disabled={!hasEditPermission}
            onCheckedChange={() => {
              if (row.published) {
                handleUnpublish([row.id])
              } else {
                handlePublish([row.id])
              }
            }}
          />
        )
      },
    },
    {
      title: 'Edit',
      accessor: '',
      render: (row) => (
        <a
          className="view text-accent-1 underline"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            if (!hasEditPermission) {
              return
            }
            setCurrentAddonId(row.id)
            setIsActive(row.status === 'ACTIVE')
            setEditMode(true)
            setEditCreateModal(true)
          }}
        >
          {row.status === 'ACTIVE' ? 'Edit' : 'View'}
        </a>
      ),
      width: '80px',
    },
  ]

  return (
    <>
      <Header
        pageTitle="Tickets Add-ons"
        actions={
          <>
            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                onClick={() => {
                  setEditCreateModal(true)
                  setCurrentAddonId(undefined)
                  setEditMode(false)
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Tickets" subtitle="Add-ons" className="mb-5" />
        <Table
          handleSortBy={handleSortBy}
          columns={columns}
          data={addons}
          columnWidth="20%"
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <CreateAddonModal
        addonId={currentAddonId}
        currency={currency?.symbol || 'RON'}
        setShowCreateModal={setEditCreateModal}
        showCreateModal={editCreateModal}
        editMode={editMode}
        setAddons={setAddons}
        setTotalElemenets={setTotalElemenets}
        isActive={isActive}
        page={page + 1}
        size={size}
        allLanguages={allLanguages}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
